import { useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { FileDown } from 'lucide-react'
import { useShiftData } from '../features/shift-handover/hooks/useShiftData'
import { useShiftMutations } from '../features/shift-handover/hooks/useShiftMutations'
import { useAuthStore } from '../features/auth/store/authStore'
import { useSelectedPropertyId } from '../store/propertyStore'
import { isEligibleManager, isShiftOwner, canForceClose } from '../features/shift-handover/utils/shiftPermissions'
import { ShiftStatusBar } from '../features/shift-handover/components/ShiftStatusBar'
import { ShiftKpiCards } from '../features/shift-handover/components/ShiftKpiCards'
import { CriticalIssues } from '../features/shift-handover/components/CriticalIssues'
import { BlockedTasks } from '../features/shift-handover/components/BlockedTasks'
import { RepeatWarnings } from '../features/shift-handover/components/RepeatWarnings'
import { SupervisorNotes } from '../features/shift-handover/components/SupervisorNotes'
import { HandoffModal } from '../features/shift-handover/components/HandoffModal'
import { PreviousShifts } from '../features/shift-handover/components/PreviousShifts'
import { ConfirmModal } from '../components/ConfirmModal'

export function HandoverPage(): JSX.Element {
  const { shift, kpis, critical, blocked, isLoading, isError } = useShiftData()
  const user = useAuthStore((s) => s.user)
  const propertyId = useSelectedPropertyId()
  const { start, updateNotes, createHandoff, forceClose } = useShiftMutations()
  const [handoffOpen, setHandoffOpen] = useState(false)
  const [forceCloseOpen, setForceCloseOpen] = useState(false)
  const notesRef = useRef<HTMLDivElement>(null)
  const exportRef = useRef<HTMLDivElement>(null)

  const owner = isShiftOwner(user, shift)

  const handleExport = async (): Promise<void> => {
    const node = exportRef.current
    if (!node) return
    if (!window.confirm('Export this shift handover as a PDF?')) return
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')])
      const pdf = new jsPDF('p', 'pt', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const sections = Array.from(node.children) as HTMLElement[]
      let cursorY = 0
      for (const section of sections) {
        const canvas = await html2canvas(section, { scale: 2, useCORS: true, backgroundColor: '#ffffff' })
        const imgW = pageWidth
        const imgH = (canvas.height * imgW) / canvas.width
        if (cursorY + imgH > pageHeight) { pdf.addPage(); cursorY = 0 }
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, cursorY, imgW, imgH)
        cursorY += imgH + 8
      }
      const dateStr = new Date().toISOString().slice(0, 10)
      pdf.save(`shift-handover-${dateStr}.pdf`)
    } catch {
      toast.error('Could not export the PDF. Please try again.')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Shift Handover</h1>
          <p className="text-sm text-gray-400">{new Date().toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' })}</p>
        </div>
        {shift && kpis && (
          <button
            type="button"
            onClick={handleExport}
            data-html2canvas-ignore="true"
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <FileDown className="h-4 w-4" /> Export PDF
          </button>
        )}
      </div>

      <ShiftStatusBar
        shift={shift}
        canStart={isEligibleManager(user, propertyId)}
        isOwner={owner}
        canForceClose={canForceClose(user)}
        isStarting={start.isPending}
        onStart={() => propertyId !== null && start.mutate({ property_id: propertyId })}
        onHandoff={() => setHandoffOpen(true)}
        onEditNotes={() => notesRef.current?.scrollIntoView({ behavior: 'smooth' })}
        onForceClose={() => setForceCloseOpen(true)}
      />

      {isLoading && <p className="text-sm text-gray-400">Loading shift…</p>}
      {isError && <p className="text-sm text-red-500">Could not load shift data. Please retry.</p>}

      {shift && kpis && (
        <div ref={exportRef} className="space-y-4">
          <ShiftKpiCards kpis={kpis} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <CriticalIssues issues={critical} />
            <BlockedTasks tasks={blocked} />
          </div>
          <RepeatWarnings />
          <div ref={notesRef}>
            <SupervisorNotes
              notes={shift.notes}
              editable={owner}
              isSaving={updateNotes.isPending}
              onSave={(value) => updateNotes.mutate({ shiftId: shift.id, notes: value })}
            />
          </div>
        </div>
      )}

      <PreviousShifts />

      <HandoffModal
        open={handoffOpen}
        onClose={() => setHandoffOpen(false)}
        isSubmitting={createHandoff.isPending}
        onSubmit={(toSupervisorUserId) => {
          if (!shift) return
          createHandoff.mutate({ shiftId: shift.id, toSupervisorUserId }, { onSuccess: () => setHandoffOpen(false) })
        }}
      />

      <ConfirmModal
        open={forceCloseOpen}
        title="Force-close shift"
        message="This ends the current shift without a handoff and auto-declines any pending handoff. This cannot be undone."
        confirmLabel="Force-close"
        destructive
        isPending={forceClose.isPending}
        onClose={() => setForceCloseOpen(false)}
        onConfirm={() => { if (shift) forceClose.mutate({ shiftId: shift.id }, { onSuccess: () => setForceCloseOpen(false) }) }}
      />
    </div>
  )
}

export default HandoverPage
